// Landing screen for a BMD diagnostics session, and the resume point the
// student comes back to. Toggles into <BmdTestPlayer> on start.
//
// Redrawn to the PM's mockup: one card carrying the whole picture - what the
// test is, how far through it the student is, which section they are in, and
// one button that continues from exactly where they stopped. The old screen
// listed clusters with status badges and no sense of progress, so a student
// resuming had no way to tell how much was left.
//
// Presentation only. Nothing about what gets served, when, or in what order
// changed; the session state and the player are untouched.
//
// THE YEAR LEVEL IS NOT SHOWN. The mockup has a "YEAR 4" eyebrow above the
// title; the BMD base year is tutor-only context and the student app must never
// surface it, so that one line is left out deliberately, pending the PM.

import React from "react"
import { Container } from "reactstrap"
import { useNavigate, useParams } from "react-router-dom"
import { useBmdSession } from "../../hooks/diagnostics/useBmdSession"
import { BmdTestPlayer } from "../../components/diagnostics/BmdTestPlayer"
import { BmdProgressBar } from "../../components/diagnostics/BmdProgressBar"
import { BmdFinishCard } from "../../components/diagnostics/BmdFinishCard"
import { BMD_UI, approxMinutes } from "../../constants/diagnostics/bmd.theme"
import {
  BMD_COPY,
  BMD_MCQ_TIMER_SECONDS,
  BMD_SECONDS_PER_QUESTION_ESTIMATE,
} from "../../constants/diagnostics/bmd.constants"
import type { BmdTopicSummary } from "../../modules/diagnostics/types/bmd.types"
import { plainText } from "../../modules/diagnostics/utils/text"

/**
 * Which pill a section shows.
 *
 * The mockup draws three states side by side - In progress, Next, Done - and
 * neither field answers it alone. What is still owed says whether anything is
 * LEFT; what has been answered says whether anything has STARTED. Reading only
 * the first would badge an untouched section "In progress", because a section
 * nobody has opened owes every one of its questions.
 *
 * Derived here, from state the session already returns. Nothing is fetched or
 * computed server-side for it.
 */
type SectionState = "done" | "active" | "upcoming"

function answeredIn(topic: BmdTopicSummary): number {
  // Every phase counts as "started": a prerequisite block holds no base
  // questions, and an FM retry is still work on this section.
  return (
    topic.answered_count +
    topic.prereq_answered_count +
    topic.retry_answered_count
  )
}

function sectionStateOf(topic: BmdTopicSummary): SectionState {
  if (topic.unserved_question_count === 0) return "done"
  return answeredIn(topic) > 0 ? "active" : "upcoming"
}

/** Questions in a section, whichever kind of test this is. */
function sectionTotal(topic: BmdTopicSummary): number {
  return topic.base_question_count > 0
    ? topic.base_question_count
    : topic.prereq_question_count
}

function sectionAnswered(topic: BmdTopicSummary): number {
  return topic.base_question_count > 0
    ? topic.answered_count
    : topic.prereq_answered_count
}

const PILL: Record<
  SectionState,
  { bg: string; text: string; label: string }
> = {
  active: {
    bg: BMD_UI.PILL_ACTIVE_BG,
    text: BMD_UI.PILL_ACTIVE_TEXT,
    label: BMD_COPY.SECTION_IN_PROGRESS,
  },
  upcoming: {
    bg: BMD_UI.PILL_IDLE_BG,
    text: BMD_UI.PILL_IDLE_TEXT,
    label: BMD_COPY.SECTION_NEXT,
  },
  done: {
    bg: BMD_UI.PILL_DONE_BG,
    text: BMD_UI.PILL_DONE_TEXT,
    label: BMD_COPY.SECTION_DONE,
  },
}

const SectionRow: React.FC<{
  index: number
  topic: BmdTopicSummary
  state: SectionState
}> = ({ index, topic, state }) => {
  const pill = PILL[state]
  const total = sectionTotal(topic)
  const answered = sectionAnswered(topic)
  const isActive = state === "active"

  return (
    <li
      className="d-flex align-items-center gap-3 p-3 mb-2"
      style={{
        border: `1px solid ${
          isActive ? BMD_UI.OPTION_SELECTED_BORDER : BMD_UI.OPTION_BORDER
        }`,
        borderRadius: 10,
        backgroundColor: isActive ? BMD_UI.OPTION_SELECTED_BG : BMD_UI.CARD_BG,
        listStyle: "none",
      }}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0"
        style={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          fontSize: 13,
          backgroundColor: isActive ? BMD_UI.FILL : "transparent",
          color: isActive ? "#fff" : BMD_UI.MUTED,
          border: isActive ? "none" : `1px solid ${BMD_UI.LETTER_BORDER}`,
        }}
      >
        {index + 1}
      </span>

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="fw-bold" style={{ color: BMD_UI.TITLE, fontSize: 15 }}>
          {plainText(topic.topic_name)}
        </div>
        {/* A bar only where it says something. At 0% or 100% it adds nothing
            the pill has not already said. */}
        {isActive ? (
          <div className="d-flex align-items-center gap-3 mt-2">
            <BmdProgressBar
              done={answered}
              total={total}
              className="flex-grow-1"
            />
            <span
              className="flex-shrink-0"
              style={{ color: BMD_UI.BODY, fontSize: 12 }}
            >
              {answered} of {total} {BMD_COPY.ANSWERED_SUFFIX}
            </span>
          </div>
        ) : (
          <div
            style={{ color: BMD_UI.BODY, fontSize: 12 }}
            className="mt-1"
          >
            {total} question{total === 1 ? "" : "s"}
          </div>
        )}
      </div>

      <span
        className="flex-shrink-0 fw-medium"
        style={{
          backgroundColor: pill.bg,
          color: pill.text,
          borderRadius: 20,
          padding: "4px 12px",
          fontSize: 12,
        }}
      >
        {pill.label}
      </span>
    </li>
  )
}

export const BmdWelcomePage: React.FC = () => {
  const { key } = useParams<{ key: string }>()
  const navigate = useNavigate()
  const diagnosticsSessionKey = key || ""
  const { session, loading, error } = useBmdSession(diagnosticsSessionKey)
  const [started, setStarted] = React.useState<boolean>(false)

  // A prerequisite test is its own activity, built from what the child got
  // wrong in an earlier diagnostic. Read from the session because the route is
  // the same for both kinds. Null-safe: renderHeader runs before the session
  // has loaded.
  const isPrereqTest = session?.session_kind === "PREREQ"

  const headerTitle = isPrereqTest
    ? BMD_COPY.PREREQ_PLAYER_TITLE
    : BMD_COPY.PLAYER_TITLE

  const renderHeader = () => (
    <div className="cs-pg-header bg-dark-blue py-4">
      <Container className="custom-wt">
        <div className="d-flex align-items-center">
          <div className="mt-0 border-right me-4" style={{ width: "20%" }}>
            <div
              className="text-white font-size-14 text-uppercase cursor-pointer"
              onClick={() => {
                if (started) {
                  setStarted(false)
                } else {
                  navigate(-1)
                }
              }}
              role="button"
            >
              <span>
                <i className="fas fa-chevron-left me-1"></i>
              </span>{" "}
              Back
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between flex-grow-1">
            <h6 className="mb-0 font-size-20 text-white w-60 fw-regular">
              {headerTitle}
            </h6>
            <p className="mb-0 text-uppercase luminous-wht">
              <span className="text-yellow me-1">Math</span>{" "}
              {isPrereqTest
                ? BMD_COPY.PREREQ_SHORT_LABEL
                : "Baseline Diagnostics"}
            </p>
          </div>
        </div>
      </Container>
    </div>
  )

  // One card shell for every state, so loading, error and loaded all sit in the
  // same place on the page instead of jumping.
  const shell = (children: React.ReactNode) => (
    <div className="page-wrapper" style={{ backgroundColor: BMD_UI.PAGE_BG }}>
      {renderHeader()}
      <Container className="custom-wt">
        <div
          className="mt-4 mb-5 p-4 p-md-5"
          style={{
            backgroundColor: BMD_UI.CARD_BG,
            borderRadius: BMD_UI.CARD_RADIUS,
            border: BMD_UI.CARD_BORDER,
          }}
        >
          {children}
        </div>
      </Container>
    </div>
  )

  if (!diagnosticsSessionKey) {
    return shell(<p className="text-danger mb-0">Missing session key.</p>)
  }
  if (loading && !session) {
    return shell(<p className="text-muted mb-0">{BMD_COPY.LOADING}</p>)
  }
  if (error) {
    return shell(<p className="text-danger mb-0">{error}</p>)
  }
  if (!session) {
    return shell(null)
  }

  if (started) {
    return shell(
      <BmdTestPlayer
        diagnosticsSessionKey={diagnosticsSessionKey}
        onSessionComplete={() => {
          // Leave the player rendered. It shows its own finish card.
        }}
        onBack={() => navigate(-1)}
      />,
    )
  }

  // A prerequisite test has no base questions. Its size lives in the prereq
  // totals, and it grows as the student gets things wrong.
  const questionTotal = isPrereqTest
    ? session.total_prereq_questions
    : session.total_base_questions
  const answeredTotal = isPrereqTest
    ? session.answered_prereq_questions
    : session.answered_base_questions
  const remaining = Math.max(0, questionTotal - answeredTotal)

  const sections = session.topics.map((t) => ({
    topic: t,
    state: sectionStateOf(t),
  }))
  const activeSection =
    sections.find((s) => s.state === "active") ||
    sections.find((s) => s.state === "upcoming") ||
    null

  // Unchanged rule: the session is finished when the server says so. The
  // remediation-pending wording is still chosen the same way, and now sits on
  // the finish card.
  const isComplete = session.flow_status === "COMPLETED"
  const remediationPending = session.topics.some(
    (t) => t.topic_status === "COMPLETED",
  )

  if (isComplete) {
    return shell(
      <BmdFinishCard
        sectionCount={session.topics.length}
        questionsAnswered={answeredTotal}
        body={
          remediationPending
            ? BMD_COPY.SESSION_PAUSED_FOR_REMEDIATION
            : isPrereqTest
            ? BMD_COPY.PREREQ_SESSION_COMPLETE
            : BMD_COPY.SESSION_COMPLETE
        }
        onBack={() => navigate(-1)}
      />,
    )
  }

  const hasStartedBefore = answeredTotal > 0

  return shell(
    <>
      <h1 className="fw-bold mb-2" style={{ color: BMD_UI.TITLE, fontSize: 28 }}>
        {headerTitle}
      </h1>
      <p style={{ color: BMD_UI.BODY, fontSize: 15 }} className="mb-4">
        {session.topics.length}{" "}
        {session.topics.length === 1
          ? BMD_COPY.SECTION_LABEL
          : BMD_COPY.SECTIONS_LABEL}
        , {questionTotal} question{questionTotal === 1 ? "" : "s"}. You get{" "}
        {BMD_MCQ_TIMER_SECONDS} seconds for each question, and you can stop
        whenever you need to.
      </p>

      <div className="d-flex justify-content-between align-items-baseline mb-2">
        <span className="fw-bold" style={{ color: BMD_UI.TITLE, fontSize: 14 }}>
          {answeredTotal} of {questionTotal} {BMD_COPY.ANSWERED_SUFFIX}
        </span>
        <span style={{ color: BMD_UI.BODY, fontSize: 13 }}>
          {remaining} left · about{" "}
          {approxMinutes(remaining, BMD_SECONDS_PER_QUESTION_ESTIMATE)} min
        </span>
      </div>
      <BmdProgressBar
        done={answeredTotal}
        total={questionTotal}
        className="mb-4"
      />

      <ul className="list-unstyled mb-4">
        {sections.map((s, i) => (
          <SectionRow
            key={s.topic.pk_session_topic_key}
            index={i}
            topic={s.topic}
            state={s.state}
          />
        ))}
      </ul>

      <button
        type="button"
        className="btn w-100 fw-bold text-white d-flex align-items-center justify-content-center gap-2"
        style={{
          backgroundColor: BMD_UI.ACCENT,
          borderColor: BMD_UI.ACCENT,
          borderRadius: 8,
          padding: "13px 20px",
          fontSize: 16,
        }}
        onClick={() => setStarted(true)}
      >
        {activeSection
          ? `${
              hasStartedBefore
                ? BMD_COPY.CONTINUE_PREFIX
                : BMD_COPY.START_PREFIX
            } ${plainText(activeSection.topic.topic_name)}`
          : isPrereqTest
          ? BMD_COPY.PREREQ_START
          : "Start Diagnostics"}
        <i className="fas fa-arrow-right" aria-hidden="true"></i>
      </button>

      <p
        className="text-center mt-3 mb-0 d-flex align-items-center justify-content-center gap-2"
        style={{ color: BMD_UI.BODY, fontSize: 13 }}
      >
        <i className="far fa-clock" aria-hidden="true"></i>
        {BMD_COPY.OVERVIEW_SAVE_NOTE}
      </p>
    </>,
  )
}

export default BmdWelcomePage
